import { Component, useEffect } from 'react'
import { Link, useRouteError } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { Padding } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { H2 } from '@/@bothrs-ui/web/components/typography/Heading'
import { appInsights } from '@/ApplicationInsights'
import { useAdminMessage } from '@/modules/healthblocks/hooks/useAdminMessage'
import { AdminMessageCard } from '@/modules/shared/components/admin-message-card/AdminMessageCard'
import { ErrorLayout } from '@/modules/shared/components/error-layout/ErrorLayout'
import { useTenantHref } from '@/modules/shared/hooks/useTenantHref'

import type { ReactNode } from 'react'

const ErrorPage = ({ message }: { message?: string }) => {
  const theme = useTheme()
  const error = useRouteError() as {
    status?: number
    message?: string
    data?: string
  }
  const tenantHref = useTenantHref()
  const homePath = tenantHref('/')

  useEffect(() => {
    if (!error || error.status === 404) return

    appInsights?.trackException({
      exception: error instanceof Error ? error : new Error(error.message || error.data || 'Unknown render error'),
      severityLevel: 3, // Error
      properties: {
        status: error.status,
        source: 'ErrorBoundary',
      },
    })
  }, [error])

  console.log('error', error)

  const errorContent =
    error?.status === 404 ? (
      <Padding
        px={theme.UI.Spacing.Space6}
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <H2>Pagina niet gevonden</H2>
        <div>
          <Button as={Link} to={homePath} variant='Primary' size='normal'>
            Terug naar beginscherm
          </Button>
        </div>
      </Padding>
    ) : (
      <Padding
        px={theme.UI.Spacing.Space6}
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <H2>Oops, something&apos;s not right.</H2>
        <p style={{ marginBottom: theme.UI.Spacing.Space4 }}>
          This issue has been reported, so we&apos;ll get this fixed ASAP! In the meantime, try restarting the
          application.
        </p>
        <pre>
          {error?.status} {message || error?.message || error?.data}
        </pre>
      </Padding>
    )

  return (
    <SafeAdminMessageBoundary fallback={errorContent}>
      <AdminMessageOverlay errorContent={errorContent} />
    </SafeAdminMessageBoundary>
  )
}

/**
 * Fetches admin message and overlays it on the error content.
 * Isolated in its own component so that if healthblocks context
 * is unavailable (e.g. ErrorPage rendered as a top-level errorElement),
 * the SafeAdminMessageBoundary catches the failure and falls back
 * to the plain error content.
 */
const AdminMessageOverlay = ({ errorContent }: { errorContent: ReactNode }) => {
  const { adminMessage } = useAdminMessage({ refreshInterval: 1000 * 60 * 1 })

  if (adminMessage?.blocking) {
    return (
      <>
        <div
          style={{
            position: 'fixed',
            inset: 0,
            filter: 'blur(2px)',
            opacity: 0.3,
            pointerEvents: 'none',
          }}
        >
          {errorContent}
        </div>
        <ErrorLayout title={adminMessage.title ?? ''} message={adminMessage.message} markdown />
      </>
    )
  }

  if (adminMessage) {
    return (
      <>
        <AdminMessageCard message={adminMessage.message} />
        {errorContent}
      </>
    )
  }

  return <>{errorContent}</>
}

class SafeAdminMessageBoundary extends Component<{ children: ReactNode; fallback: ReactNode }, { hasError: boolean }> {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback
    }
    return this.props.children
  }
}

export { ErrorPage }
